import React, { createContext, useContext, useState, useCallback } from 'react';

//Ref: https://anilchitte.medium.com/securing-your-react-application-a-guide-to-jwt-protected-routes-and-token-refresh-26cc53a6d421

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const baseUrl = '';  // Replace with your backend API URL
    //const { fetch: originalFetch } = window;

    // Function to get the access token (could be from sessionStorage or a cookie management utility)
    const getAccessToken = () => {
        return sessionStorage.getItem('accessToken');   // Or read from HttpOnly cookie via a backend endpoint
    };
    // Function to set the access token
    const setAccessToken = (jwt) => {
        sessionStorage.setItem('accessToken', jwt.token); // Or set HttpOnly cookie via a backend endpoint
        if (jwt.persistToken) {
            localStorage.setItem('accessToken', jwt.token);
            localStorage.setItem('refreshToken', jwt.refreshToken); // Store refresh token if not HttpOnly
            localStorage.setItem('refreshTokenExpiry', jwt.refreshTokenExpiry.toLocaleString()); // Store refresh token if not HttpOnly
        }
    };

    const check4PreviousAuthToken = async () => {
        var accessToken = localStorage.getItem('accessToken');
        var refreshToken = localStorage.getItem('refreshToken');
        if (accessToken != null && refreshToken != null) {
            var refreshTokenExpiry = new Date(localStorage.getItem('refreshTokenExpiry'));
            if (refreshTokenExpiry >= new Date()) {
                await refreshAccessTokenFromServer(accessToken, refreshToken)
            }
        }
    }

    const getUserInfo = () => {
        let userInfoStr = sessionStorage.getItem('user');
        if (userInfoStr !== "undefined")
            return JSON.parse(userInfoStr); // Or read from HttpOnly cookie via a backend endpoint
    };
    const setUserInfo = (info) => {
        sessionStorage.setItem('user', JSON.stringify(info)); // Or set HttpOnly cookie via a backend endpoint
    };

    // Function to remove tokens on logout
    const clearAuthTokens = () => {
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('user'); 
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshTokenExpiry');
        // Future Enhancement - Invalidate refresh token on backend (important for security)
        //fetch(baseUrl + 'api/auth/logout');
    };

    const refreshAccessTokenFromServer = async (accessToken, refreshToken)=> {
        clearAuthTokens();
        const options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessToken, refreshToken }) // Send refresh token if not using HttpOnly cookie
        };
        const refreshResponse = await fetch(baseUrl + 'api/auth/Refresh', options);
        // eslint-disable-next-line no-unused-vars
        const { jwt, user } = await refreshResponse.json();
        setAccessToken(jwt);
        setUserInfo(user);
        // ToDo: Retry original request with new token
    }

    const secureFetch = async (...args) => {
        let [resource, config] = args;
        if (!config) config = {};

        // Request interceptor logic
        const accessToken = getAccessToken();
        if (accessToken) {
            if (!config.headers)
                config.headers = {};
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        else {
            config.credentials = 'include'; // Important for sending cookies (if using HttpOnly refresh token)
        }
        console.log(`Sending request to: ${resource}`);
        const response = await fetch(resource, config);

        // Response interceptor logic here (e.g., error handling, logging response)
        if (!response.ok) {
            console.error(`Error response from: ${resource}`, response);
            if (response.status === 401) {
                const refreshToken = localStorage.getItem('refreshToken'); // Or it will be sent automatically via HttpOnly cookie
                if (accessToken && !refreshToken && !config.credentials) { // If not using HttpOnly cookie and refresh token not found
                    await refreshAccessTokenFromServer(accessToken, refreshToken);
                }
            }
        }
        
        return response;
    };

    const login = useCallback(async (credentials) => {
        try {
            setLoading(true);
            const options = {
                method: 'POST',
                headers: {
                    //"Content-Type": "application/x-www-form-urlencoded"
                    'Content-Type': 'application/json'
                },
                //body: new URLSearchParams(params).toString()
                body: JSON.stringify(credentials)
            };
            const response = await fetch(baseUrl + 'api/auth/Login', options);
            const { jwt, user } = await response.json();
            setAccessToken(jwt);
            setUserInfo(user);
            return true; // Indicate successful login
        } catch (err) {
            console.error('Login failed:', err);
            clearAuthTokens();
            return false; // Indicate failed login
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        clearAuthTokens();
    }, []);

    const value = {
        getUserInfo,
        secureFetch,
        getAccessToken,
        check4PreviousAuthToken,
        login,
        logout
    };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
export const useAuth = () => {
    return useContext(AuthContext);
};